import {
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { IconButton, SkeletonText } from 'utoniq-ui'

import {
  Maybe,
  PageInfo,
  PickupUserEventFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenuProps } from '@/components/layouts'
import { SideMenu } from '@/components/layouts'
import { EventCategory } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { DateTime } from '@/core/entities'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  events: {
    edges: {
      node: PickupUserEventFragment
    }[]
    pageInfo: Pick<PageInfo, 'hasNextPage' | 'endCursor'>
  } | null
  user: Maybe<SideMenuProps['user']>
  isArtist: boolean
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const MyOwnedTicketsEventSelectionTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('myOwnedTicketsEventSelection')
  const { routerNames } = useRouter()
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)

  return (
    <BaseLayout
      margin
      header={{
        title: t('title'),
        left: config.applicationType === 'organization' && (
          <IconButton.AvatarNoImage
            edge="start"
            onClick={() => setOpenSideMenu(!openSideMenu)}
            bgcolor="grey.50"
            badge={
              state.user?.hasManagementTask ||
              !!state.user?.unreadNotificationCount ||
              !!state.user?.isMembershipPersonalInformationRequired
            }
          />
        ),
        back: config.applicationType === 'standalone',
      }}
      navigation={{
        type: 'global',
        props: {
          hasManagementTask: !!state.user?.hasManagementTask,
          unreadNotificationCount: state.user?.unreadNotificationCount,
          hasSettingTask: !!state.user?.isMembershipPersonalInformationRequired,
        },
      }}
    >
      <Head title={routerNames.MyOwnedTicketsEventSelection} />
      {state.loading && !state.events ? (
        <Stack sx={{ py: 2 }}>
          <SkeletonText variant="large" width="75%" />
        </Stack>
      ) : (
        <Typography variant="h4" sx={{ py: 2 }}>
          {t('selectEvent', 'イベントを選択してください')}
        </Typography>
      )}
      <ScrollFetcher
        nodes={state.events?.edges.map(({ node }) => (
          <StoreOrderCell event={node} key={node.id} />
        ))}
        loading={state.loading}
        hasNextPage={state.hasNextPage}
        onFetchMore={onFetchMore}
        loadingComponent={
          <Stack spacing={2}>
            <StoreOrderSkeleton />
            <StoreOrderSkeleton />
            <StoreOrderSkeleton />
            <StoreOrderSkeleton />
          </Stack>
        }
        emptyMessage={t(
          'myOwnedTicketsEventEmptyMessage',
          '受け取りが必要な商品はありません。'
        )}
      />
      <SideMenu
        open={openSideMenu}
        onClose={() => setOpenSideMenu(false)}
        user={state.user}
        followArtists={state.followArtists}
        memberArtists={state.memberArtists}
        isArtist={state.isArtist}
      />
    </BaseLayout>
  )
}

type StoreOrderCellProps = {
  event: PickupUserEventFragment
}
const StoreOrderCell: Cell<StoreOrderCellProps> = ({ event }) => {
  const { t } = useTranslation('myOwnedTicketsEventSelection')
  const { routerPages, generatePath } = useRouter()

  if (!event) return null

  // 次の受け取り時間
  const nextPickupTime = event.eventCollections.edges
    .map(({ node }) => {
      const nextTime = node.myPickupUsers
        ?.filter(
          ({ pickupTime }) => pickupTime > DateTime.nowUnixMilliseconds()
        )
        ?.sort((a, b) => a.pickupTime - b.pickupTime)[0]
      return {
        pickupTime: nextTime?.pickupTime,
        formattedPickupStartEndTime: nextTime?.formattedPickupStartEndTime,
      }
    })
    .filter(({ pickupTime }) => !!pickupTime)
    .sort((a, b) => a.pickupTime - b.pickupTime)[0]

  return (
    <CellFrame>
      <Card elevation={0}>
        <CardActionArea
          href={generatePath(routerPages.MyOwnedTickets, {
            eventId: event.id,
          })}
        >
          <CardContent sx={{ py: 2, px: 4, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" spacing={3}>
              <Avatar
                variant="square"
                src={event.thumbnail?.smallUri ?? ''}
                sx={{ width: 100, height: 64 }}
              />
              <Stack spacing={1}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <EventCategory event={event} />
                  <Typography variant="overline" color="text.secondary">
                    {t('eventDate', '開催日')} {event.aboutDateRange}
                  </Typography>
                </Stack>
                <Typography variant="subtitle1">{event.title}</Typography>
                {nextPickupTime?.formattedPickupStartEndTime && (
                  <Typography variant="overline" color="text.secondary">
                    {t('nextPickupTime', '次の受け取り時間')}
                    {nextPickupTime?.formattedPickupStartEndTime}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </CellFrame>
  )
}

const StoreOrderSkeleton: Cell = () => (
  <CellFrame>
    <Card elevation={0}>
      <CardContent sx={{ py: 2, px: 4, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" spacing={3}>
          <Skeleton variant="rectangular" width={100} height={64} />
          <Stack spacing={1} flex={1}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Stack flex={1}>
                <SkeletonText variant="medium" />
              </Stack>
              <Stack flex={3}>
                <SkeletonText variant="medium" width="80%" />
              </Stack>
            </Stack>
            <SkeletonText variant="subtitle1" />
            <SkeletonText variant="caption" width="75%" />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  </CellFrame>
)
